/**
 * @file        src/components/ImageUpload.jsx
 * @description Reusable image field: pick a file → uploads to /api/uploads →
 *              stores the returned absolute URL. Also accepts a pasted URL.
 *              Shows a live preview. Used by blogs, packages, etc.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (shared image upload UI).
 *   - 2026-08-04 Claude — Optional `check` prop enforces pixel dimensions and
 *                          file size BEFORE uploading (logo/favicon fields need
 *                          shape guarantees to render correctly — a rejected
 *                          file never reaches the server). Existing uses pass
 *                          no `check` and behave exactly as before.
 */
import { useRef, useState } from 'react';
import Icon from './Icon';
import { post } from '../api/client';
import { toast } from '../lib/toast';

/** Read a file's pixel dimensions without uploading it. */
function readDims(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Not a readable image')); };
    img.src = url;
  });
}

/**
 * `check` (all optional): { minW, minH, maxW, maxH, square, maxBytes }.
 * Returns an error message, or null when the file passes.
 */
async function violation(file, check) {
  if (!check) return null;
  if (check.maxBytes && file.size > check.maxBytes) {
    return `File is ${Math.round(file.size / 1024)} KB — keep it under ${Math.round(check.maxBytes / 1024)} KB.`;
  }
  const { w, h } = await readDims(file);
  if (check.square && w !== h) return `Must be square — this file is ${w}×${h}px.`;
  if ((check.minW && w < check.minW) || (check.minH && h < check.minH)) {
    return `Too small (${w}×${h}px) — minimum is ${check.minW || 'any'}×${check.minH || 'any'}px.`;
  }
  if ((check.maxW && w > check.maxW) || (check.maxH && h > check.maxH)) {
    return `Too large (${w}×${h}px) — maximum is ${check.maxW || 'any'}×${check.maxH || 'any'}px.`;
  }
  return null;
}

export default function ImageUpload({ value, onChange, check }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function onPick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const problem = await violation(file, check);
      if (problem) {
        toast.err('Image rejected', problem);
        if (inputRef.current) inputRef.current.value = '';
        return;
      }
    } catch {
      toast.err('Image rejected', 'This file could not be read as an image.');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const res = await post('/uploads', fd);
      onChange(res.data.url);
    } catch (err) {
      toast.err('Upload failed', err.response?.data?.error?.message || 'Could not upload image');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="img-upload">
      <div className="img-upload-row">
        <div className="img-thumb">
          {value ? <img src={value} alt="preview" /> : <span className="muted">No image</span>}
        </div>
        <div className="img-upload-actions">
          <button type="button" className="btn ghost" disabled={busy} onClick={() => inputRef.current?.click()}>
            <Icon name="download" /> {busy ? 'Uploading…' : 'Choose File'}
          </button>
          {value ? (
            <button type="button" className="btn ghost" onClick={() => onChange('')}>Remove</button>
          ) : null}
        </div>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={onPick} />
      </div>
      <input
        className="img-url"
        placeholder="…or paste an image URL"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
